import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { Patient } from '@/types';
import { Plus, Edit, Trash } from 'lucide-react';
import { Textarea } from "@/components/ui/textarea";
import { countries, states, cities } from '@/data/locationData';
import { patientService } from '@/services/patientService';

const PatientManagement: React.FC = () => {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [currentPatient, setCurrentPatient] = useState<Patient | null>(null);
  const [newPatient, setNewPatient] = useState<Omit<Patient, 'id' | 'createdAt'>>({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    country: '',
    zipCode: '',
    notes: '',
    gender: '',
    age: undefined
  });
  
  const { toast } = useToast();
  
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [selectedState, setSelectedState] = useState<string>('');
  
  // Load patients on component mount
  useEffect(() => {
    const loadPatients = async () => {
      try {
        setIsLoading(true);
        const data = await patientService.getAll();
        setPatients(data);
      } catch (error) {
        toast({
          title: "Failed to load patients",
          description: "Please try again later.",
          variant: "destructive"
        });
        console.error('Error loading patients:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadPatients();
  }, [toast]);

  const handleCountryChange = (value: string) => {
    setSelectedCountry(value);
    setSelectedState(''); // Reset state when country changes
    setNewPatient(prev => ({
      ...prev,
      country: countries.find(c => c.id === value)?.name || '',
      state: '', // Reset state name
      city: '' // Reset city name
    }));
  };
  
  const handleStateChange = (value: string) => {
    setSelectedState(value);
    setNewPatient(prev => ({
      ...prev,
      state: states[selectedCountry as keyof typeof states]?.find(s => s.id === value)?.name || '',
      city: '' // Reset city when state changes
    }));
  };
  
  const handleCityChange = (value: string) => {
    setNewPatient(prev => ({
      ...prev,
      city: cities[selectedState as keyof typeof cities]?.find(c => c.id === value)?.name || ''
    }));
  };
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewPatient({
      ...newPatient,
      [name]: name === 'age' ? (value ? parseInt(value, 10) : undefined) : value
    });
  };
  
  const handleSelectChange = (name: string, value: string) => {
    setNewPatient({
      ...newPatient,
      [name]: value
    });
  };
  
  const handleAddPatient = async () => {
    try {
      const patientToAdd = await patientService.create(newPatient);
      setPatients([...patients, patientToAdd]);
      setIsEditDialogOpen(false);
      toast({
        title: "Patient added",
        description: `${newPatient.fullName} has been added to the system.`
      });
      resetForm();
    } catch (error) {
      toast({
        title: "Failed to add patient",
        description: "Please try again later.",
        variant: "destructive"
      });
      console.error('Error adding patient:', error);
    }
  };
  
  const handleEditPatient = (patient: Patient) => {
    setCurrentPatient(patient);
    setNewPatient({
      fullName: patient.fullName,
      email: patient.email,
      phone: patient.phone,
      address: patient.address || '',
      city: patient.city || '',
      state: patient.state || '',
      country: patient.country || '',
      zipCode: patient.zipCode || '',
      notes: patient.notes || '',
      gender: patient.gender || '',
      age: patient.age
    });
    
    // Set country and state selects if available
    if (patient.country) {
      const countryObj = countries.find(c => c.name === patient.country);
      if (countryObj) {
        setSelectedCountry(countryObj.id);
      }
    }
    
    if (patient.state && patient.country) {
      const countryObj = countries.find(c => c.name === patient.country);
      if (countryObj && states[countryObj.id as keyof typeof states]) {
        const stateObj = states[countryObj.id as keyof typeof states].find(s => s.name === patient.state);
        if (stateObj) {
          setSelectedState(stateObj.id);
        }
      }
    }
    
    setIsEditDialogOpen(true);
  };
  
  const handleUpdatePatient = async () => {
    if (!currentPatient) return;
    
    try {
      const updatedPatient = await patientService.update(currentPatient.id, newPatient);
      const updatedPatients = patients.map(patient => 
        patient.id === currentPatient.id ? updatedPatient : patient
      );
      
      setPatients(updatedPatients);
      setIsEditDialogOpen(false);
      toast({
        title: "Patient updated",
        description: `${newPatient.fullName}'s information has been updated.`
      });
      resetForm();
    } catch (error) {
      toast({
        title: "Failed to update patient",
        description: "Please try again later.",
        variant: "destructive"
      });
      console.error('Error updating patient:', error);
    }
  };
  
  const handleDeletePrompt = (patient: Patient) => {
    setCurrentPatient(patient);
    setIsDeleteDialogOpen(true);
  };
  
  const handleDeletePatient = async () => {
    if (!currentPatient) return;
    
    try {
      await patientService.delete(currentPatient.id);
      const filteredPatients = patients.filter(patient => patient.id !== currentPatient.id);
      setPatients(filteredPatients);
      setIsDeleteDialogOpen(false);
      toast({
        title: "Patient removed",
        description: `${currentPatient.fullName} has been removed from the system.`,
        variant: "destructive"
      });
    } catch (error) {
      toast({
        title: "Failed to delete patient",
        description: "Please try again later.",
        variant: "destructive"
      });
      console.error('Error deleting patient:', error);
    }
  };
  
  const resetForm = () => {
    setCurrentPatient(null);
    setSelectedCountry('');
    setSelectedState('');
    setNewPatient({
      fullName: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      country: '',
      zipCode: '',
      notes: '',
      gender: '',
      age: undefined
    });
  };
  
  return (
    <div className="container mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Patient Management</h1>
        <Dialog open={isEditDialogOpen && !currentPatient} onOpenChange={open => setIsEditDialogOpen(open)}>
          <DialogTrigger asChild>
            <Button variant="default" className="gap-2">
              <Plus className="h-4 w-4" />
              Add Patient
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Add New Patient</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              {/* Required Personal Information */}
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <label htmlFor="fullName">Full Name <span className="text-destructive">*</span></label>
                  <Input 
                    id="fullName" 
                    name="fullName"
                    value={newPatient.fullName} 
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <label htmlFor="phone">Phone Number <span className="text-destructive">*</span></label>
                  <Input 
                    id="phone" 
                    name="phone"
                    value={newPatient.phone} 
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <label htmlFor="gender">Gender <span className="text-destructive">*</span></label>
                  <Select 
                    onValueChange={(value) => handleSelectChange('gender', value)}
                    value={newPatient.gender}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Female">Female</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <label htmlFor="age">Age <span className="text-destructive">*</span></label>
                  <Input 
                    id="age" 
                    name="age"
                    type="number"
                    value={newPatient.age || ''} 
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <label htmlFor="country">Country <span className="text-destructive">*</span></label>
                  <Select
                    onValueChange={handleCountryChange}
                    value={selectedCountry}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map(country => (
                        <SelectItem key={country.id} value={country.id}>
                          {country.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <label htmlFor="email">Email Address</label>
                  <Input 
                    id="email" 
                    name="email"
                    type="email"
                    value={newPatient.email} 
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              {/* Optional Address Information */}
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <label htmlFor="state">State/Province</label>
                  <Select
                    onValueChange={handleStateChange}
                    value={selectedState}
                    disabled={!selectedCountry}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedCountry && states[selectedCountry as keyof typeof states]?.map(state => (
                        <SelectItem key={state.id} value={state.id}>
                          {state.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <label htmlFor="city">City</label>
                  <Select
                    onValueChange={handleCityChange}
                    value={newPatient.city}
                    disabled={!selectedState}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select city" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedState && cities[selectedState as keyof typeof cities]?.map(city => (
                        <SelectItem key={city.id} value={city.id}>
                          {city.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <label htmlFor="address">Street Address</label>
                  <Input 
                    id="address" 
                    name="address"
                    value={newPatient.address} 
                    onChange={handleInputChange}
                  />
                </div>
                <div className="grid gap-2">
                  <label htmlFor="zipCode">ZIP Code</label>
                  <Input 
                    id="zipCode" 
                    name="zipCode"
                    value={newPatient.zipCode} 
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="grid gap-2">
                <label htmlFor="notes">Notes</label>
                <Textarea 
                  id="notes" 
                  name="notes"
                  value={newPatient.notes} 
                  onChange={handleInputChange}
                  placeholder="Add any additional notes here..."
                  className="min-h-[100px]"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleAddPatient}>Save Patient</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Patient Dialog - Update with the same fields as Add Patient */}
        <Dialog open={isEditDialogOpen && !!currentPatient} onOpenChange={open => {
          if (!open) resetForm();
          setIsEditDialogOpen(open);
        }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Edit Patient Information</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              {/* Same form fields as Add Patient dialog */}
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <label htmlFor="edit-fullName">Full Name <span className="text-destructive">*</span></label>
                  <Input 
                    id="edit-fullName" 
                    name="fullName"
                    value={newPatient.fullName} 
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <label htmlFor="edit-phone">Phone Number <span className="text-destructive">*</span></label>
                  <Input 
                    id="edit-phone" 
                    name="phone"
                    value={newPatient.phone} 
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <label htmlFor="edit-gender">Gender <span className="text-destructive">*</span></label>
                  <Select 
                    onValueChange={(value) => handleSelectChange('gender', value)}
                    value={newPatient.gender}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Female">Female</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <label htmlFor="edit-age">Age <span className="text-destructive">*</span></label>
                  <Input 
                    id="edit-age" 
                    name="age"
                    type="number"
                    value={newPatient.age || ''} 
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <label htmlFor="country">Country <span className="text-destructive">*</span></label>
                  <Select
                    onValueChange={handleCountryChange}
                    value={selectedCountry}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map(country => (
                        <SelectItem key={country.id} value={country.id}>
                          {country.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <label htmlFor="edit-email">Email Address</label>
                  <Input 
                    id="edit-email" 
                    name="email"
                    type="email"
                    value={newPatient.email} 
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              {/* Optional Address Information */}
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <label htmlFor="state">State/Province</label>
                  <Select
                    onValueChange={handleStateChange}
                    value={selectedState}
                    disabled={!selectedCountry}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedCountry && states[selectedCountry as keyof typeof states]?.map(state => (
                        <SelectItem key={state.id} value={state.id}>
                          {state.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <label htmlFor="city">City</label>
                  <Select
                    onValueChange={handleCityChange}
                    value={newPatient.city}
                    disabled={!selectedState}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select city" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedState && cities[selectedState as keyof typeof cities]?.map(city => (
                        <SelectItem key={city.id} value={city.id}>
                          {city.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <label htmlFor="edit-address">Street Address</label>
                  <Input 
                    id="edit-address" 
                    name="address"
                    value={newPatient.address} 
                    onChange={handleInputChange}
                  />
                </div>
                <div className="grid gap-2">
                  <label htmlFor="edit-zipCode">ZIP Code</label>
                  <Input 
                    id="edit-zipCode" 
                    name="zipCode"
                    value={newPatient.zipCode} 
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="grid gap-2">
                <label htmlFor="edit-notes">Notes</label>
                <Textarea 
                  id="edit-notes" 
                  name="notes"
                  value={newPatient.notes} 
                  onChange={handleInputChange}
                  placeholder="Add any additional notes here..."
                  className="min-h-[100px]"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setIsEditDialogOpen(false);
                resetForm();
              }}>Cancel</Button>
              <Button onClick={handleUpdatePatient}>Update Patient</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm Deletion</DialogTitle>
            </DialogHeader>
            <p>
              Are you sure you want to delete {currentPatient?.fullName}'s record? This action cannot be undone.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleDeletePatient}>Delete</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>City</TableHead>
              <TableHead>State</TableHead>
              <TableHead>Country</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">
                  Loading patients...
                </TableCell>
              </TableRow>
            ) : patients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  No patients found. Add your first patient.
                </TableCell>
              </TableRow>
            ) : (
              patients.map(patient => (
                <TableRow key={patient.id}>
                  <TableCell className="font-medium">{patient.fullName}</TableCell>
                  <TableCell>{patient.email}</TableCell>
                  <TableCell>{patient.phone}</TableCell>
                  <TableCell>{patient.city}</TableCell>
                  <TableCell>{patient.state}</TableCell>
                  <TableCell>{patient.country}</TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        onClick={() => handleEditPatient(patient)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        onClick={() => handleDeletePrompt(patient)}
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default PatientManagement;
